import { createFileRoute, Link, useRouter } from '@tanstack/react-router';
import { format } from 'date-fns';
import {
    ArrowLeft,
    ChevronLeft,
    ChevronRight,
    Gavel,
    Loader2,
    SquareArrowOutUpRight,
    X,
} from 'lucide-react';
import { useState } from 'react';

import courtroomVideo from '@apps/web/assets/agent_court_phoebe_wright.mp4';
import { buildDefaultDateRange } from '@apps/web/components/admin/accounts/trend_chart';
import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import { type DateRangePickerValue } from '@apps/web/components/ui/date_range_picker';
import { cn } from '@apps/web/lib/utils';
import {
    humanLabel,
    justiceVerdictMeta,
    STATUS_LABELS,
    statusBadgeClass,
    TRIGGER_TYPE_LABELS,
} from '@apps/web/routes/_app/admin/agent-court/agent_court_display';
import { RootCauseTable } from '@apps/web/routes/_app/admin/agent-court/root_cause_table';
import {
    useAdminAgentCourtCases,
    useAdminAgentCourtRootCauses,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import { type components } from '@libraries/typescript/api_client';

const PER_PAGE = 50;
// Root-cause window default, matching the agent-analysis page's month view.
const ROOT_CAUSE_WINDOW_DAYS = 30;
type AgentCourtCaseListItem = components['schemas']['AgentCourtCaseListItem'];
type AgentCourtSearch = {
    readonly page: number;
    readonly root_cause?: string;
    // Set when the Agent Analysis page navigates here (the "Top things
    // to fix" card); a back arrow then returns to that page.
    readonly from_analysis?: boolean;
};

export const Route = createFileRoute('/_app/admin/agent-court/')({
    component: AgentCourtPage,
    head: () => ({ meta: [{ title: 'Agent Court' }] }),
    validateSearch: (search: Record<string, unknown>): AgentCourtSearch => {
        const page = Number(search['page']);
        const rootCause = search['root_cause'];
        return {
            page: Number.isFinite(page) && page >= 1 ? Math.floor(page) : 1,
            root_cause:
                typeof rootCause === 'string' && rootCause.length > 0
                    ? rootCause
                    : undefined,
            from_analysis: search['from_analysis'] === true ? true : undefined,
        };
    },
});

function AgentCourtPage() {
    const router = useRouter();
    const navigate = Route.useNavigate();
    const {
        page,
        root_cause: selectedRootCauseId,
        from_analysis: fromAnalysis,
    } = Route.useSearch();

    const [rootCauseDateRange, setRootCauseDateRange] =
        useState<DateRangePickerValue>(() =>
            buildDefaultDateRange(ROOT_CAUSE_WINDOW_DAYS),
        );

    const { data, isLoading, error } = useAdminAgentCourtCases({
        page,
        per_page: PER_PAGE,
        root_cause_id: selectedRootCauseId,
    });
    const { data: rootCauseData } = useAdminAgentCourtRootCauses(
        rootCauseDateRange.from,
        rootCauseDateRange.to,
    );
    const cases = data?.cases ?? [];
    const total = data?.total ?? 0;
    const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));
    const hasPreviousPage = page > 1;
    const hasNextPage = total > page * PER_PAGE;
    // Landing filtered (e.g. from the Agent Analysis "Top things to fix"
    // card), the page should open as that root cause's results: the
    // easter-egg video and the root-cause picker give way to a banner
    // naming the filter, with the filtered case list directly below it.
    const selectedRootCause = selectedRootCauseId
        ? (rootCauseData?.root_causes.find(
              (rootCause) => rootCause.id === selectedRootCauseId,
          ) ?? null)
        : null;

    return (
        <div className="flex flex-col gap-4 p-6">
            {!selectedRootCauseId && (
                <div className="flex justify-center">
                    {/* Courtroom easter egg: Phoebe presents the evidence. */}
                    <video
                        className="w-full max-w-xl rounded-lg"
                        src={courtroomVideo}
                        autoPlay={true}
                        loop={true}
                        muted={true}
                        playsInline={true}
                        aria-hidden="true"
                    />
                </div>
            )}
            <div className="flex flex-wrap items-center gap-2">
                {fromAnalysis ? (
                    // Arrived from the Agent Analysis page: return there —
                    // via history when possible so its calendar range
                    // survives — mirroring the trace page's back arrow.
                    <Button
                        variant="ghost"
                        size="sm"
                        aria-label="Back to agent analysis"
                        onClick={() => {
                            if (router.history.canGoBack()) {
                                router.history.back();
                                return;
                            }
                            void navigate({ to: '/admin/agent-analysis' });
                        }}
                    >
                        <ArrowLeft className="h-4 w-4" />
                    </Button>
                ) : null}
                <Gavel className="h-5 w-5" />
                <h1 className="text-lg font-semibold">Agent Court</h1>
                {!isLoading && <Badge variant="secondary">{total}</Badge>}
                <span className="text-muted-foreground text-sm">
                    Read-only review of Justice cases and recommendations.
                </span>
            </div>
            {selectedRootCauseId ? (
                <div className="bg-muted/40 flex flex-wrap items-start justify-between gap-3 rounded-xl border p-3">
                    <div>
                        <div className="text-sm font-semibold">
                            {selectedRootCause?.title ?? 'One root cause'}
                        </div>
                        {selectedRootCause ? (
                            <div className="text-muted-foreground text-sm">
                                {selectedRootCause.description}
                            </div>
                        ) : null}
                        <div className="text-muted-foreground mt-1 text-xs">
                            {isLoading
                                ? 'Loading cases…'
                                : `Every case below shares this root cause (${total} ${total === 1 ? 'case' : 'cases'}).`}
                        </div>
                    </div>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                            navigate({
                                search: (current) => ({
                                    ...current,
                                    root_cause: undefined,
                                    page: 1,
                                }),
                            })
                        }
                    >
                        <X className="h-3 w-3" />
                        Show all cases
                    </Button>
                </div>
            ) : (
                <RootCauseTable
                    rootCauses={rootCauseData?.root_causes ?? []}
                    unclusteredCaseCount={
                        rootCauseData?.unclustered_case_count ?? 0
                    }
                    dateRange={rootCauseDateRange}
                    onDateRangeChange={setRootCauseDateRange}
                    selectedRootCauseId={null}
                    onSelectRootCause={(rootCauseId) =>
                        navigate({
                            search: (current) => ({
                                ...current,
                                root_cause: rootCauseId ?? undefined,
                                page: 1,
                            }),
                        })
                    }
                />
            )}
            <div className="text-muted-foreground flex items-center justify-between text-sm">
                <span>
                    Page {page} of {totalPages}
                </span>
                <div className="flex gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        disabled={!hasPreviousPage}
                        onClick={() =>
                            navigate({
                                search: (current) => ({
                                    ...current,
                                    page: page - 1,
                                }),
                            })
                        }
                    >
                        <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        disabled={!hasNextPage}
                        onClick={() =>
                            navigate({
                                search: (current) => ({
                                    ...current,
                                    page: page + 1,
                                }),
                            })
                        }
                    >
                        <ChevronRight className="h-4 w-4" />
                    </Button>
                </div>
            </div>
            {isLoading ? (
                <div className="flex items-center justify-center py-12">
                    <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
                </div>
            ) : error ? (
                <div className="text-muted-foreground py-12 text-center text-sm">
                    Failed to load Agent Court cases.
                </div>
            ) : cases.length === 0 ? (
                <div className="text-muted-foreground py-12 text-center text-sm">
                    No Agent Court cases found.
                </div>
            ) : (
                <div className="overflow-x-auto rounded-xl border">
                    <table className="w-full text-sm">
                        <thead className="bg-muted/50 text-muted-foreground text-xs">
                            <tr>
                                <th className="px-3 py-2 text-left font-medium">
                                    Trigger
                                </th>
                                <th className="px-3 py-2 text-left font-medium">
                                    Organization
                                </th>
                                <th className="px-3 py-2 text-left font-medium">
                                    Source trace
                                </th>
                                <th className="px-3 py-2 text-left font-medium">
                                    LLM Judge
                                </th>
                                <th className="px-3 py-2 text-left font-medium">
                                    Justice
                                </th>
                                <th className="px-3 py-2 text-right font-medium">
                                    Recs
                                </th>
                                <th className="px-3 py-2 text-left font-medium">
                                    Updated
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {cases.map((agentCourtCase) => (
                                <AgentCourtRow
                                    key={agentCourtCase.id}
                                    agentCourtCase={agentCourtCase}
                                    onSelect={() =>
                                        router.navigate({
                                            to: '/admin/agent-court/$caseId',
                                            params: {
                                                caseId: agentCourtCase.id,
                                            },
                                            // Carry the list view state so
                                            // the detail page's back arrow
                                            // restores this exact list.
                                            search: {
                                                page,
                                                root_cause: selectedRootCauseId,
                                                from_analysis: fromAnalysis,
                                            },
                                        })
                                    }
                                />
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function AgentCourtRow({
    agentCourtCase,
    onSelect,
}: {
    readonly agentCourtCase: AgentCourtCaseListItem;
    readonly onSelect: () => void;
}) {
    const verdictMeta = justiceVerdictMeta(agentCourtCase.justice_verdict);
    return (
        <tr
            className="hover:bg-muted/40 cursor-pointer border-t"
            onClick={onSelect}
        >
            <td className="px-3 py-2">
                {humanLabel(TRIGGER_TYPE_LABELS, agentCourtCase.trigger_type)}
            </td>
            <td className="px-3 py-2">
                {agentCourtCase.organization_internal_name ??
                    agentCourtCase.organization_name ??
                    'Unknown org'}
            </td>
            <td className="px-3 py-2">
                {agentCourtCase.source_agent_conversation_id ? (
                    <Link
                        to="/admin/agent-traces/$conversationId"
                        params={{
                            conversationId:
                                agentCourtCase.source_agent_conversation_id,
                        }}
                        className="text-primary inline-flex items-center gap-1 underline"
                        onClick={(event) => event.stopPropagation()}
                    >
                        View trace
                        <SquareArrowOutUpRight className="h-3 w-3" />
                    </Link>
                ) : (
                    <span className="text-muted-foreground">—</span>
                )}
            </td>
            <td className="px-3 py-2">
                {agentCourtCase.llm_judge_verdict ??
                    (agentCourtCase.has_llm_judge_result ? 'Reviewed' : '—')}
            </td>
            <td className="px-3 py-2">
                <div className="flex flex-col gap-1">
                    <Badge
                        variant="secondary"
                        className={cn(
                            'w-fit text-xs',
                            statusBadgeClass(agentCourtCase.status),
                        )}
                    >
                        {humanLabel(STATUS_LABELS, agentCourtCase.status)}
                    </Badge>
                    {verdictMeta ? (
                        <Badge
                            variant="outline"
                            className={cn(
                                'w-fit text-xs',
                                verdictMeta.badgeClassName,
                            )}
                        >
                            {verdictMeta.label}
                        </Badge>
                    ) : null}
                </div>
            </td>
            <td className="px-3 py-2 text-right tabular-nums">
                {agentCourtCase.recommendation_count}
            </td>
            <td className="text-muted-foreground px-3 py-2">
                {format(new Date(agentCourtCase.updated_at), 'MMM d, h:mm a')}
            </td>
        </tr>
    );
}
